import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { publicApi, Pattern, Demonstration, getMediaUrl } from '../services/api';
import DemonstrationCard from '../components/DemonstrationCard';
import { SkeletonDetail } from '../components/Skeleton';
import DemonstrationUploadModal from '../components/DemonstrationUploadModal';
import VideoPlayer from '../components/VideoPlayer';
import MediaDialog, { ShortcutHint } from '../components/MediaDialog';
import FeedbackThread from '../components/FeedbackThread';
import { useAuth } from '../context/AuthContext';
import { useMembership } from '../hooks/useMembership';
import PlanRequired from '../components/PlanRequired';

const PatternPage: React.FC = () => {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const hasLibraryAccess = !!user?.role;
  const { needsPlan } = useMembership();

  const [pattern, setPattern] = useState<Pattern | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [demonstrations, setDemonstrations] = useState<Demonstration[]>([]);
  const [demosLoading, setDemosLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedDemo, setSelectedDemo] = useState<Demonstration | null>(null);

  useEffect(() => {
    if (!idOrSlug) return;
    setLoading(true);
    setError(null);
    publicApi.patterns
      .getById(idOrSlug)
      .then((data) => setPattern(data))
      .catch(() => setError('Failed to load pattern'))
      .finally(() => setLoading(false));
  }, [idOrSlug]);

  useEffect(() => {
    if (!pattern) return;
    setDemosLoading(true);
    publicApi.demonstrations
      .getByPattern(pattern.id)
      .then(setDemonstrations)
      .catch(() => {})
      .finally(() => setDemosLoading(false));
  }, [pattern]);

  const refreshDemos = () => {
    if (!pattern) return;
    publicApi.demonstrations.getByPattern(pattern.id).then(setDemonstrations).catch(() => {});
  };

  if (loading) {
    return (
      <div className="pattern-page">
        <SkeletonDetail />
      </div>
    );
  }
  if (error || !pattern) {
    return (
      <div className="error-state pattern-error-state">
        <p>{error ?? 'Pattern not found'}</p>
        <Link to="/library">Back to library</Link>
      </div>
    );
  }

  return (
    <div className="move-page pattern-page">
      <div className="move-header">
        <h1>{pattern.title}</h1>
        {pattern.classContext && (
          <p className="pattern-class-context">
            {pattern.classContext}
            {pattern.classDate ? ` · ${new Date(pattern.classDate).toLocaleDateString()}` : ''}
          </p>
        )}
        <div className="move-meta pattern-meta-row">
          {pattern.style?.name && <span className="badge">{pattern.style.name}</span>}
          {pattern.skillLevel?.name && <span className="badge">{pattern.skillLevel.name}</span>}
          {pattern.instructor?.name && <span className="badge">by {pattern.instructor.name}</span>}
        </div>
        {pattern.description && <p>{pattern.description}</p>}
      </div>

      {hasLibraryAccess && pattern.locked && (
        <div className="pattern-section">
          <PlanRequired perk="library" />
        </div>
      )}

      {(pattern.videoUrl || pattern.thumbnail) && (
        <div className="pattern-video-wrap">
          {pattern.videoUrl && hasLibraryAccess ? (
            // Intrinsic sizing keeps portrait and landscape clean, without baked letterboxing.
            <VideoPlayer
              src={getMediaUrl(pattern.videoUrl)}
              poster={pattern.thumbnail ? getMediaUrl(pattern.thumbnail) : undefined}
              title={pattern.title}
              loop
              intrinsic
              className="pattern-video"
            />
          ) : pattern.thumbnail ? (
            // Unauthenticated (or videoUrl stripped): show the poster with a sign-in prompt on click.
            <img
              src={getMediaUrl(pattern.thumbnail)}
              alt={`${pattern.title} teaching video`}
              onClick={() => !hasLibraryAccess && navigate('/login')}
              className="pattern-video-poster"
              style={{ cursor: !hasLibraryAccess ? 'pointer' : 'default' }}
              {...(!hasLibraryAccess
                ? {
                    role: 'button',
                    tabIndex: 0,
                    'aria-label': `Sign in to watch ${pattern.title}`,
                    onKeyDown: (e: React.KeyboardEvent<HTMLImageElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate('/login');
                      }
                    },
                  }
                : {})}
            />
          ) : null}
          {!hasLibraryAccess && pattern.thumbnail && (
            <p className="pattern-signin-hint">
              Sign in as a dancer to watch the full pattern video.
            </p>
          )}
        </div>
      )}

      <section className="pattern-section">
        <h2>Move Sequence</h2>
        {pattern.moves && pattern.moves.length > 0 ? (
          <ol className="pattern-move-list">
            {pattern.moves.map((pm) => (
              <li key={pm.id} className="pattern-move-item">
                <Link to={`/library/moves/${pm.move.id}`} className="pattern-move-link">
                  {pm.move.title}
                </Link>
                {pm.move.style?.name && <span className="pattern-move-style"> · {pm.move.style.name}</span>}
                {pm.notes && <div className="pattern-move-notes">{pm.notes}</div>}
              </li>
            ))}
          </ol>
        ) : (
          <p className="pattern-muted">No moves in this pattern yet.</p>
        )}
      </section>

      <section className="pattern-section">
        <div className="pattern-section-header">
          <h2>Demonstrations</h2>
          {isAuthenticated && (
            needsPlan('feedback') ? (
              <Link to="/join" className="btn btn-primary">Join to get feedback</Link>
            ) : (
              <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
                Demonstrate This Pattern
              </button>
            )
          )}
          {!isAuthenticated && (
            <button className="btn btn-primary" onClick={() => navigate('/login')}>
              Sign in to upload
            </button>
          )}
        </div>

        {demosLoading ? (
          <div className="loading">Loading demonstrations…</div>
        ) : demonstrations.length === 0 ? (
          <p className="pattern-muted">No demonstrations yet — be the first!</p>
        ) : (
          <div className="pattern-demos-grid">
            {demonstrations.map((demo) => (
              <DemonstrationCard
                key={demo.id}
                demonstration={demo}
                onSelect={() => setSelectedDemo(demo)}
                showMove={false}
              />
            ))}
          </div>
        )}
      </section>

      {showUpload && (
        <DemonstrationUploadModal
          pattern={pattern}
          onClose={() => setShowUpload(false)}
          onSuccess={() => {
            setShowUpload(false);
            refreshDemos();
          }}
        />
      )}

      {selectedDemo && (
        <MediaDialog
          src={getMediaUrl(selectedDemo.videoUrl)}
          poster={selectedDemo.thumbnail ? getMediaUrl(selectedDemo.thumbnail) : undefined}
          title={`Demonstration by ${selectedDemo.dancer?.name || 'a dancer'}`}
          onClose={() => setSelectedDemo(null)}
        >
          <div className="media-dialog-person">
            {selectedDemo.dancer?.profileImage ? (
              <img src={selectedDemo.dancer.profileImage} alt="" className="media-dialog-avatar" />
            ) : (
              <span className="media-dialog-avatar" aria-hidden="true">
                {selectedDemo.dancer?.name?.charAt(0) || '?'}
              </span>
            )}
            <div>
              <strong>{selectedDemo.dancer?.name}</strong>
              <small>Demonstrating {pattern.title}</small>
            </div>
          </div>
          {selectedDemo.caption && <p className="media-dialog-quote">{selectedDemo.caption}</p>}
          <FeedbackThread demonstration={selectedDemo} />
          <ShortcutHint />
        </MediaDialog>
      )}
    </div>
  );
};

export default PatternPage;
